import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Select, TYPE, Value } from "baseui/select"
import { ListStyle_Types, CreateListStyle, FieldTypes, CreateFieldTypes, FieldStyles, CreateFieldStyle, SelectFieldOption_Values } from "../../Definitions/"
import { colors } from "../../Styles"

// #region | SelectField Component
interface SelectField_Props {
  context?: string
  placeholder?: string
  defaultValue?: string | number
  options?: SelectFieldOption_Values[];
  startEnhancer?: string | JSX.Element
  overrides?: {
    textStart?: boolean
    inTable?: boolean
    fieldStyle?: FieldStyles | string
    fieldType?: FieldTypes | string
    fieldWidth?: string
    dropdownWidth?: string
    dropdownLeft?: string
    dropdownBorderRadius?: string
  }
  isCreatable?: boolean // OBS: Requires isSearchable={true}
  isClearable?: boolean
  isSearchable?: boolean
  isDisabled?: boolean
  onChange?:(e:Value)=>void
}

export interface SelectField_Handles {
  context: string 
  setContext: (e:string) => void,
}
/**
 * SelectField
 * @param placeholder text in inputfield before typing if there is no defaultValue.
 * @param fieldStyle color styling for inputfield, e.g. fieldStyle="blue".
 * @param startEnhancer start icon.
 * @param options selectoptions (list items) in the selector aka data set.
 * @param textStart if true the text will move to the left where the startenhancer would have been.
 * @param isCreatable (OBS: Requires isSearchable={true}) if true it will be possible to type whatever and add it as a selected value.
 * @param isClearable if true a "clear icon" will appear in the end of the field if the field has a value.
 * @param defaultValue value to select from start. 
 * @param options one select in the list consisting of an id, a laben and maybe a domain.
 * @param overrides style overrides.
 * @param onChange function to handle the value, e.g. setDomain.
 * @returns a BaseUi selector, build from content param. 
 */
const _SelectField:React.ForwardRefRenderFunction<SelectField_Handles, SelectField_Props> = ({ context, placeholder, startEnhancer, isCreatable = false, isClearable = false, isSearchable = false, defaultValue, options = [], overrides, onChange, isDisabled }: SelectField_Props, ref) => {
  const [value, setValue] = useState<Value>([])
  const [contextValue, setContextValue] = useState(context?context:"")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  useImperativeHandle(ref, ()=>({
    context: contextValue,
    setContext(e:string) { setContextValue(e) }
  }))
  
  // FieldStyle
  const { backgroundColor, hoverColor, fontStyle, dropdownColor, scrollbarColor } = useMemo(() => {
    var _Style = CreateFieldStyle(overrides?.fieldStyle)
    return _Style
  }, [overrides?.fieldStyle])

  // FieldType
  const { width, height, distancePaddingSelect, dropdownBorder, arrowMargin, dropdownBorderRadius, optionContentMargin, dropdownWidth, optionContentWidth, popoverBorderRadius, popoverWidth, popoverTop, popoverPosition, dropdownListItemWidth, dropdownBoxShadow, showSelectArrow, controleContainerBorderRadiusOpen } = useMemo(() => {
    var _Style = CreateFieldTypes(overrides?.fieldType)
    return _Style
  }, [overrides?.fieldType])

  const _fieldWidth = overrides?.fieldWidth ? overrides?.fieldWidth : width
  const _dropdownWidth = overrides?.dropdownWidth ? overrides?.dropdownWidth : _fieldWidth
  const _dropdownLeft = overrides?.dropdownLeft ? overrides?.dropdownLeft : "0px"
  const _dropdownBorderRadius = overrides?.dropdownBorderRadius ? overrides?.dropdownBorderRadius : dropdownBorderRadius

  // Options
  const selectOptions = useMemo(() => {
    const optionsList: SelectFieldOption_Values[] = options.map((option, index) => ({
      id: option.id?option.id:index,
      label: option.label,
      domain: option.domain
    }));
    var Default = optionsList.find(e => e.label === defaultValue)
    if (Default) setValue([Default])
    return optionsList
  }, [defaultValue, options])

  // Handle padding based on the presence of the startEnhancer
  const handleDistance = () => {
    if (!startEnhancer && overrides?.textStart) {
      return { padding: "0 0 0 19px" }
    } else if (!startEnhancer && overrides?.textStart && overrides?.inTable) {
      return { padding: "2px 0 0 14px"}
    } else {
      return { padding: distancePaddingSelect }
    }
  }

  const EditField = () => {
    if (overrides?.inTable) {
      return { margin: "0px 0px 0px -5px" }
      } else {
        return { margin: "0px" }
      }
    }

  return (
    <div style={{ position: "relative", width: width}}>
      {/* startEnhancer */}
      <div>
        {!startEnhancer ? (<></>) : (
          startEnhancer === "string" ?
          <img alt="start_icn" src={startEnhancer}
            style={{
              width: "25px",
              height: "25px",
              position: "absolute",
              zIndex: "2",
              top: "2.5px",
              left: "22px"
            }}
          />
          :
          <div style={{position: "absolute", zIndex: "2", top: "2.5px", left: "22px"}}>
            {startEnhancer}
          </div>
        )}
      </div>
      {/* selectfield */}
      <div>
        <Select
          placeholder={placeholder}
          creatable={isCreatable}
          clearable={isClearable}
          searchable={isSearchable}
          disabled={isDisabled}
          options={selectOptions}
          onChange={(e) => {
            if (e.value) {
              setValue(e.value);
              if (onChange) onChange(e.value);
            } else {
              setValue([]);
            }
          }}

          labelKey="label"
          valueKey="id"
          value={value}
          
          onOpen={() => setIsDropdownOpen(true)} // Set dropdown state
          onClose={() => setIsDropdownOpen(false)} // Unset dropdown state
          
          ignoreCase={false}
          type={TYPE.select}

          getOptionLabel={({ option }: any) => {
            return (
              <div style={{ display: 'flex', alignItems: 'center', ...option.style }}>
                {option.label}
              </div>
            );
          }}

          // selectfield overrides
          overrides={{
            Root: { style: () => Object.assign({}, {
              width: _fieldWidth,
              ...EditField()
            } as React.CSSProperties )},
            ControlContainer: { style: () => Object.assign({}, {
              ...backgroundColor,
              borderRadius: isDropdownOpen ? controleContainerBorderRadiusOpen : "10px",
              height: height,
              display: "flex",
              alignItems: "center",
              // boxSizing: "unset",
              border: "none",
              zIndex: "1"
            } as React.CSSProperties )},
            ValueContainer: { style: () => Object.assign({}, {
              ...fontStyle,
              ...handleDistance()
            } as React.CSSProperties )},
            Input: { style: () => Object.assign({}, {
              ...fontStyle,
              lineHeight: "25px !important",
              paddingLeft: "2px",
              'input::-webkit-inner-spin-button':{
                appearance: 'none',
              },
              'input::-webkit-outer-spin-button':{
                appearance: 'none',
              },
              '-moz-appearance': 'textfield', // For Firefox
              ':children': {lineHeight: "25px !important",}
            } as React.CSSProperties )},
            SingleValue: { style: () => Object.assign({}, {
              ...fontStyle,
              margin: "0px 0px 0px 2px"
            } as React.CSSProperties )},
            Placeholder: { style: () => Object.assign({}, {
              ...fontStyle,
              opacity: "30%",
              marginLeft: "2px"
            } as React.CSSProperties )},
            OptionContent: { style: () => Object.assign({}, { 
              ...fontStyle,
              width: `calc(${_dropdownWidth} - 30px)`,
              boxSizing: "border-box",
              padding: "10px 16px",
              margin: optionContentMargin,
              backgroundColor: colors.transparent,
              ':hover': { ...hoverColor, borderRadius: "10px" }
            } as React.CSSProperties )},
            DropdownListItem: { style: () => Object.assign({}, { 
              backgroundColor: colors.transparent,
              width: _dropdownWidth,
              padding: "0px",
              display: "flex",
              justifyContent: "center"
            } as React.CSSProperties )},
            Dropdown: { style: () => Object.assign({}, {
              ...dropdownColor,
              ...scrollbarColor,
              position: "absolute",
              width: _dropdownWidth,
              minWidth: _dropdownWidth,
              borderRadius: _dropdownBorderRadius,
              // boxSizing: "unset",
              boxShadow: dropdownBoxShadow,
              display: "flex",
              overflowY: "auto",
              overflowX: "hidden",
              flexDirection: "column",
              alignItems: "center",
              scrollbarWidth: "thin",
              maxHeight: "122px",
              height: "auto",
              border: dropdownBorder
            } as React.CSSProperties )},
            Popover: {
              props: {
                overrides: {
                  Inner: { style: () => Object.assign({}, {
                    ...backgroundColor,
                    borderRadius: popoverBorderRadius,
                  } as React.CSSProperties )},
                  Body: { style: () => Object.assign({}, {
                    ...backgroundColor,
                    borderRadius: popoverBorderRadius,
                    top: popoverTop,
                    position: popoverPosition,
                    width: _fieldWidth,
                    maxHeight: "122px",
                    height: "auto",
                    boxShadow: dropdownBoxShadow,
                    zIndex: "9999",
                    left: _dropdownLeft
                  } as React.CSSProperties )}
                }
              }
            },
            SelectArrow: {
              props: {
                overrides: {
                  Svg: { style: () => Object.assign({}, {
                      ...fontStyle,
                      marginRight: arrowMargin,
                      display: showSelectArrow
                    } as React.CSSProperties )}
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
}

const SelectField = forwardRef(_SelectField)
export default SelectField
// #endregion

// SelectFieldList Component
interface SelectFieldList_Props {
  placeholder: string[]
  options?: SelectFieldOption_Values[][]
  isCreatable?: boolean[]
  isClearable?: boolean[]
  startEnhancer?: JSX.Element[]
  listStyle?: ListStyle_Types|string
  listGap?: string
  overrides?: {
    textStart?: boolean
    inTable?: boolean
    fieldStyle?: FieldStyles | string
    fieldType?: FieldTypes | string
    fieldWidth?: string
    dropdownWidth?: string
    dropdownLeft?: string
    dropdownBorderRadius?: string
  }
}

export type SelectFieldList_PropHandle = {
  context: (index:number) => string|undefined
  refs: React.MutableRefObject<SelectField_Handles[]>
}
/**
 * SelectFieldList
 * @param placeholder text in inputfield before typing if there is no defaultValue.
 * @param fieldStyle color styling for inputfield, e.g. fieldStyle="blue".
 * @param startEnhancer start icon.
 * @param textStart if true the text will move to the left where the startenhancer would have been.
 * @param isCreatable if true it will be possible to type whatever and add it as a selected value.
 * @param isClearable if true a "clear icon" will appear in the end of the field if the field has a value.
 * @param options one select in the list consisting of an id, a laben and maybe a domain.
 * @param listStyle defines the direction of the SelectFields in the List and the seperator between them.
 * @returns A list of select fields.
 */

const _SelectFieldList:React.ForwardRefRenderFunction<SelectFieldList_PropHandle, SelectFieldList_Props> = ({placeholder, startEnhancer, isCreatable, isClearable, options = [], listStyle, listGap, overrides }: SelectFieldList_Props, ref) => {
  const inputRefs = useRef<SelectField_Handles[]>([])
  const _listGap = listGap ? { gap: listGap } : { gap: "5px" };
  const { listDirection } = useMemo(() => CreateListStyle(listStyle), [listStyle])

  // Exposing methods and references to the parent component via ref
  useImperativeHandle(ref, ()=>({
    context(index=0) {
      if (inputRefs.current && inputRefs.current[index]) return inputRefs.current[index].context
      return undefined
    },
    refs:inputRefs
  }))

  const Item_List = useMemo(() => {
    return placeholder.map((_text, context_Index) => {
      const _type = overrides?.fieldStyle&&overrides?.fieldStyle[context_Index] ? overrides.fieldStyle[context_Index] : 'text'
      const _textstart = overrides?.textStart&&overrides?.textStart ? overrides.textStart : undefined
      var _startImage = startEnhancer&&startEnhancer[context_Index] ? startEnhancer[context_Index] : undefined
      const _options = options && options[context_Index] ? options[context_Index] : []
      const _isCreatable = isCreatable&&isCreatable[context_Index] ? isCreatable[context_Index] : undefined
      const _isClearable = isClearable&&isClearable[context_Index] ? isClearable[context_Index] : undefined

      var view = [(
        <SelectField key={"List_Input_" + context_Index} ref={(el) => el?inputRefs.current[context_Index] = el:{}}
        placeholder={_text}
        startEnhancer={_startImage}
        isCreatable={_isCreatable}
        isClearable={_isClearable}
        options={_options}
        overrides={{
          textStart: _textstart,
          fieldStyle: _type
        }}
        />
      )]
      // Seperator position based on list style for the list where last item will not have the seperator
      // if (placeholder.length !== context_Index + 1) view.push(<div style={seperatorPosition as React.CSSProperties}/>)
        return view
      })
  }, [placeholder, overrides?.fieldStyle, overrides?.textStart, startEnhancer, options, isCreatable, isClearable])
  return (
    // return of div with list direction based on list style
    <div style={{...listDirection as React.CSSProperties, ..._listGap}}>
      {Item_List}
    </div>
  )
}
export const SelectFieldList = forwardRef(_SelectFieldList)

// function AnotherRootNavigation(navigator: NavigateFunction, login: any) {
//   throw new Error("Function not implemented.");
// }